import React, {InputHTMLAttributes, useCallback, useState} from 'react';
import {GridColDef, GridPagination, GridToolbarContainer,} from '@mui/x-data-grid-pro';
import {
    Checkbox,
    CheckboxProps,
    Divider,
    IconButton,
    IconButtonProps,
    styled,
    Tooltip,
    Typography,
    TypographyProps,
} from '@mui/material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import AdminDataGridColumnsMenu from '../AdminDataGrid/AdminDataGridColumnsMenu';
import {Refresh, ViewWeek} from '@mui/icons-material';
import {useTranslation} from 'react-i18next';
import {GridSlotsComponentsProps} from '@mui/x-data-grid';
import * as MaterialUIIcons from '../../controls/MaterialUIIcons';
import {CheckBoxOutlinedIcon} from '../../controls/MaterialUIIcons';
import {checkBoxStyle} from '../../controls/MultiSelect/TreeModalMultiSelect';
import useTheme from '@mui/styles/useTheme';
import AdminDataGridFilter from '../AdminDataGrid/AdminDataGridFilter';
import {Filter, FilterGroup} from '../../../utils/filter';

export enum locationButtons {
    start,
    beforePagination,
    afterPagination
}

type CustomToolbarProps = GridSlotsComponentsProps &
    (ColumnsSettingsProps | { disableColumnSettings: true }) & {
    headerName: string;
    rowCount?: number;
    disabled?: boolean;
    testId?: string;
    toolbarPagination?: boolean;
    toolbarRowCount?: boolean;
    flexGrowHeaderText?: number;
    refresh: () => void;
    showRefresh: boolean;
    headerTextProps?: TypographyProps;
    refreshTooltip?: string;
    addButton?: IconButtonProps;
    historyCheckbox?: ToolbarCheckboxProps;
    buttons: JSX.Element | { location: locationButtons, element: JSX.Element }[]; // we can use this array for manage location for the button
    startButtons: JSX.Element;
    onChangeSelectedFilter: (filter: { parameters: Filter }) => void;
    defaultFilters: FilterGroup[];
    showFilter?: boolean;
    currentFilter?: { parameters: Filter };
};

type ColumnsSettingsProps = {
    resetColumnSettings: () => void;
    updateColumns: (columns: GridColDef[]) => void;
    columns: GridColDef[];
    disableColumnSettings: false;
};

export const TypographyHeaderText = styled(Typography)(({theme}) => ({
    flexGrow: 1,
    fontWeight: 'bold',
    padding: 0,
    whiteSpace: 'nowrap',
}));

export const StyledDivider = styled(Divider)(({theme}) => ({
    border: `0.2em solid ${theme.palette.text}`,
}));

export const GridToolbarContainerToolbar = styled(GridToolbarContainer)(
    ({theme}) => ({
        backgroundColor: theme.palette.background.default,
        padding: '0 .5rem 0 0.5rem',
    })
);

const StyledGridPagination = styled(GridPagination)(({theme}) => ({
    color: theme.palette.primary.main,
    fontSize: '12px',
    '& :is(.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows)': {
        fontSize: '12px',
    },
    '& :is(.MuiInputBase-root, .MuiTablePagination-actions)': {
        margin: 0,
    },
}));

export function EditPanelDataGridToolbar({
                                             headerName,
                                             rowCount,
                                             refresh,
                                             addButton,
                                             disabled = false,
                                             historyCheckbox,
                                             flexGrowHeaderText,
                                             toolbarPagination = false,
                                             testId = '',
                                             showRefresh = true,
                                             refreshTooltip = 'Frisk opp',
                                             toolbarRowCount = false,
                                             buttons,
                                             headerTextProps,
                                             disableColumnSettings = false,
                                             startButtons,
                                             onChangeSelectedFilter,
                                             defaultFilters,
                                             showFilter = false,
                                             currentFilter,
                                             ...props
                                         }: CustomToolbarProps) {
    const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);
    const [filterMenuOpen, setFilterMenuOpen] = useState(false);

    const [columnsMenuAnchorEl, setColumnsMenuAnchorEl] =
        useState<HTMLButtonElement>();
    const {t} = useTranslation();

    const {resetColumnSettings, updateColumns, columns, ...others} =
        props as ColumnsSettingsProps;

    const getButtons = useCallback((location: locationButtons) => {
        const buttonsWithLocation = buttons as { location: locationButtons, element: JSX.Element }[];
        if (!buttonsWithLocation?.length)
            return;
        switch (location) {
            case locationButtons.afterPagination:
                return buttonsWithLocation?.filter(i => i.location === locationButtons.afterPagination)?.map(i => i.element);
            case locationButtons.beforePagination:
                return buttonsWithLocation?.filter(i => i.location === locationButtons.beforePagination)?.map(i => i.element);
            case locationButtons.start:
                return buttonsWithLocation?.filter(i => i.location === locationButtons.start)?.map(i => i.element);
        }
    }, [buttons]);
    return (
        <div>
            <GridToolbarContainerToolbar {...others} style={{minHeight: '34px'}}>
                <TypographyHeaderText
                    style={{flexGrow: flexGrowHeaderText}}
                    variant="body2"
                    marginBottom={0}
                    gutterBottom
                    hidden={!headerName}
                    {...headerTextProps}
                >
                    {toolbarRowCount && rowCount != null && rowCount > 0
                        ? `${headerName} (${rowCount})`
                        : headerName}
                </TypographyHeaderText>
                <div
                    style={{width: '100%', display: 'flex', justifyContent: 'flex-end'}}
                >
                    {startButtons}
                    {historyCheckbox && (
                        <ToolbarCheckbox
                            {...historyCheckbox}
                            titleProps={{fontWeight: '600', marginRight: '5px'}}
                            color={!historyCheckbox?.disabled ? 'lightBlue' : 'default'}
                            title="Vis historikk"
                            name="kunValgte"
                            testId={'-visHistorikk-'}
                        />
                    )}
                    {addButton && (
                        <Tooltip title={addButton.title ?? 'Legg til ny'}>
                            <IconButton
                                disabled={disabled}
                                {...addButton}
                                title={undefined}
                                test-id={testId + '-leggTillNy-btn'}
                                component="span"
                            >
                                <AddCircleOutlineOutlinedIcon
                                    color={addButton.disabled ? 'disabled' : 'primary'}
                                />
                            </IconButton>
                        </Tooltip>
                    )}
                    {React.isValidElement(buttons) ? buttons : getButtons(locationButtons.beforePagination)}
                    {toolbarPagination && <StyledGridPagination/>}
                    {getButtons(locationButtons.afterPagination)}
                    {((defaultFilters && defaultFilters.length !== 0) ||
                        showFilter) && (
                        <Tooltip title={'Filter'}>
                            <IconButton
                                onClick={(evt) => {
                                    setColumnsMenuAnchorEl(evt.currentTarget);
                                    setFilterMenuOpen(true);
                                }}
                                test-id={testId + '-filter-btn'}
                                aria-label={t('Filter')}
                            >
                                <MaterialUIIcons.FilterAltIcon color="primary"/>
                            </IconButton>
                        </Tooltip>
                    )}
                    {!disableColumnSettings && (
                        <>
                            {
                                (!!refresh && showRefresh) &&
                                <Tooltip title={`${t(refreshTooltip)}`}>
                                    <IconButton
                                        onClick={refresh}
                                        disabled={disabled}
                                        test-id={testId + '-friskOpp-btn'}
                                        aria-label={t(refreshTooltip)}
                                        component={'span'}
                                    >
                                        <Refresh color={disabled ? 'disabled' : 'primary'}/>
                                    </IconButton>
                                </Tooltip>}

                            <Tooltip title={`${t('Kolonner')}`}>
                                <IconButton
                                    onClick={(evt: any) => {
                                        setColumnsMenuAnchorEl(evt.currentTarget);
                                        setColumnsMenuOpen(true);
                                    }}
                                    test-id={testId + '-kolonner-btn'}
                                    aria-label={t('Kolonner')}
                                >
                                    <ViewWeek color={disabled ? 'disabled' : 'primary'}/>
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                </div>
            </GridToolbarContainerToolbar>
            <StyledDivider light={true}/>
            {!disableColumnSettings && (
                <AdminDataGridColumnsMenu
                    updateColumns={updateColumns}
                    autoPlacement={true}
                    testId={testId}
                    columns={columns}
                    isTreeView={false}
                    open={columnsMenuOpen}
                    anchorEl={columnsMenuAnchorEl}
                    onClose={() => setColumnsMenuOpen(false)}
                    reset={resetColumnSettings}
                />
            )}
            {showFilter &&
                <AdminDataGridFilter
                    onChangeSelectedFilter={onChangeSelectedFilter}
                    defaultFilters={defaultFilters}
                    anchorEl={columnsMenuAnchorEl}
                    open={filterMenuOpen}
                    onClose={() => setFilterMenuOpen(false)}
                />
            }

        </div>
    );
}

interface ToolbarCheckboxProps
    extends Omit<CheckboxProps,
        'icon' | 'checkedIcon' | 'onChange' | 'color' | 'value'> {
    title: string;
    titleProps?: TypographyProps;
    value: boolean;
    name: string;
    testId: string;
    onChange: (e: any, checked: boolean) => void;
    color?: 'default' | 'lightBlue';
}

export const ToolbarCheckbox = (props: ToolbarCheckboxProps) => {
    const checkBoxClasses = checkBoxStyle();
    const theme = useTheme();
    const {titleProps, onChange, value, title, name, testId, color, ...other} =
        props;
    return (
        <div
            style={{
                color: theme.palette.primary.main,
                display: 'flex',
                alignItems: 'center',
            }}
        >
            <Checkbox
                {...other}
                sx={{
                    m: 0,
                    pt: 0,
                    mb: 1,
                    mt: 1,
                    pb: 0,
                    pointerEvents: 'auto',
                }}
                style={{height: '34px', marginBottom: 0, marginTop: 0, padding: 4}}
                className={
                    props.color === 'lightBlue' ? checkBoxClasses.icon : undefined
                }
                name={name}
                checked={value ?? false}
                value={value ?? false}
                onChange={onChange}
                checkedIcon={<CheckBoxOutlinedIcon/>}
                inputProps={
                    {
                        'test-id': testId + '-toolbar-chk',
                    } as InputHTMLAttributes<HTMLInputElement>
                }
            />
            {!other.hidden && (
                <Typography fontSize="0.875rem" fontWeight="bold" {...titleProps}>
                    {title}
                </Typography>
            )}
        </div>
    );
};
